import React, { Component } from 'react';
import { Select } from 'grab-ui';
import _get from 'lodash/get';
import _debounce from 'lodash/debounce';
import _isEmpty from 'lodash/isEmpty';
import _trim from 'lodash/trim';
import classnames from 'classnames';
import {
  SCRIBE_EVENT_PARAMS,
  SCRIBE_POI_SEARCH_EVENTS,
  SCRIBE_STATE_NAMES,
  scribeTrackEvent,
} from 'applications/scribe/instrumentations/track';
import { string, func, bool, number, oneOfType, shape } from 'prop-types';
import { fetchAddress } from 'helper/address-search';
import DeleteIcon from 'public/icons/delete-cross.svg';
import {
  reverseSearchOptionsBuilder,
  keywordSearchOptionsBuilder,
  loadingOption,
} from './options-builder';
import css from './address-field.less';
import NumberedPin from '../common/numbered-pin';

class AddressField extends Component {
  constructor(props) {
    super(props);

    this.state = {
      addressEntity: null,
      rawResult: [],
      addressOptions: [],
      addressMap: {},
    };
  }

  componentDidMount() {
    const { initialValue } = this.props;
    if (!_isEmpty(initialValue)) {
      this.setIntialValue(initialValue);
    }
  }

  onAddressSearch = _debounce(async (keyword, callback = undefined) => {
    const { city, company, isPickupAddress, fetchAddressFunc } = this.props;
    const trimmedKW = _trim(keyword);
    if (_isEmpty(trimmedKW)) {
      this.setState({
        addressOptions: [],
        addressMap: {},
      });
      return;
    }
    this.setState({
      keyword: trimmedKW,
      addressOptions: [loadingOption],
    });
    const { data, isReverse } = fetchAddressFunc
      ? await fetchAddressFunc(trimmedKW)
      : await fetchAddress(trimmedKW, city, company);

    const optionsBuilder = isReverse
      ? reverseSearchOptionsBuilder
      : keywordSearchOptionsBuilder;
    const addressFieldData = optionsBuilder(data.resp, city, isPickupAddress);

    this.setState(
      {
        rawResult: isReverse ? [] : _get(data, 'resp.result', []),
        addressOptions: addressFieldData.addressOptions,
        addressMap: addressFieldData.addressMap,
      },
      callback,
    );
  }, 200);

  onSelectAddress = addressMap => coordsKey => {
    // TODO: this would NOT work if address is a root POI
    // refer to the key generation in https://gitlab.myteksi.net/engineering/web-kit/-/blob/master/web-app/packages/ge-web-booking/components/v2/address-field/options-builder.jsx#L118
    // example: https://jira.grab.com/browse/GFOM-9898
    // possible solution: compare only the coordinates instead of the coordsKey
    const addressEntity = addressMap[coordsKey];
    if (!addressEntity) {
      return;
    }
    const { onSelectAddress, index, isPickupAddress } = this.props;
    const { keyword, rawResult } = this.state;
    onSelectAddress(index, addressEntity);
    this.setState({
      addressEntity,
    });

    const poiOptions = _get(this.selectRef, 'props.children', []).map(
      option => option.key,
    );
    if (
      addressEntity &&
      poiOptions &&
      rawResult &&
      poiOptions.indexOf &&
      rawResult.map
    ) {
      scribeTrackEvent({
        stateName: SCRIBE_STATE_NAMES.EXPRESS_POI_SEARCH_WEB,
        eventName: SCRIBE_POI_SEARCH_EVENTS.SELECT_POI_FROM_RESULT,
        facets: {
          [SCRIBE_EVENT_PARAMS.CHARACTER_TYPED]: keyword,
          [SCRIBE_EVENT_PARAMS.POI_TYPE]: isPickupAddress
            ? 'PICKUP'
            : 'DROPOFF',
          [SCRIBE_EVENT_PARAMS.POI_ID]: addressEntity.id,
          [SCRIBE_EVENT_PARAMS.SELECT_POI_INDEX]: poiOptions.indexOf(coordsKey),
          [SCRIBE_EVENT_PARAMS.RECOMMEND_POI_LIST]: rawResult.map(
            poi => poi.id,
          ),
          [SCRIBE_EVENT_PARAMS.VERSION]: 'v1',
        },
      });
    }
  };

  onDelete = () => {
    const { onDelete, index } = this.props;
    onDelete && onDelete(index);
  };

  setIntialValue = async initialValue => {
    if (typeof initialValue === 'string') {
      await this.onAddressSearch(initialValue, () => {
        const [lat, lng] = initialValue.split(',');
        const key = JSON.stringify({
          latitude: parseFloat(lat),
          longitude: parseFloat(lng),
        });
        this.onSelectAddress(this.state.addressMap)(key);
      });
    } else {
      if (!initialValue) {
        return;
      }
      const { keywords, address } = initialValue;
      this.setState({
        addressEntity: {
          address: { name: keywords, combined_address: address },
        },
      });
    }
  };

  renderSelectedAddress = showDeleteButton => {
    const { isShopifyMSD, shopifyRecipient } = this.props;
    const { addressEntity } = this.state;
    const name = _get(addressEntity, ['address', 'name']);
    const combinedAddress = _get(addressEntity, [
      'address',
      'combined_address',
    ]);
    return (
      (name &&
        combinedAddress && (
          <p
            className={classnames(css.renderedAddress, {
              [css.withDeleteButton]: showDeleteButton,
            })}
          >
            {isShopifyMSD &&
              shopifyRecipient && (
                <span className={classnames(css.shopifyRecipient)}>
                  {shopifyRecipient} •&nbsp;
                </span>
              )}
            {name} • {combinedAddress}
          </p>
        )) ||
      undefined
    );
  };

  setSelectRef = ref => {
    this.selectRef = ref;
  };

  render() {
    const { addressOptions, addressMap } = this.state;
    const {
      placeholder,
      enableDelete,
      onFocus,
      index,
      showNumbers,
      isSender,
      isShopifyMSD,
    } = this.props;
    const selectedAddress = this.renderSelectedAddress(enableDelete);

    return (
      <div className={classnames(css.addressContainer)}>
        <NumberedPin
          className={css.numberedPinOverwrite}
          isSender={isSender}
          showNumber={showNumbers}
          number={index + 1}
        />
        <Select
          ref={this.setSelectRef}
          showSearch
          placeholder={placeholder}
          defaultActiveFirstOption={false}
          showArrow={false}
          filterOption={false}
          onSearch={this.onAddressSearch}
          onFocus={onFocus}
          onSelect={this.onSelectAddress(addressMap)}
          value={selectedAddress}
          className={classnames(css.addressField, css.addressFieldInputType, {
            shopifyMSD: isShopifyMSD,
          })}
          dropdownClassName={
            _isEmpty(addressOptions) ? css.emptySelect : css.addressDropdown
          }
          optionLabelProp="label"
          size="large"
          disabled={isShopifyMSD}
        >
          {addressOptions}
        </Select>
        {enableDelete && (
          <button className={css.addressClearButton} onClick={this.onDelete}>
            <img alt="delete-address" src={DeleteIcon} />
          </button>
        )}
      </div>
    );
  }
}

AddressField.propTypes = {
  index: number.isRequired,
  // initialValue if string should be lat,lng if provided. e.g. "-6.12,100.1"
  initialValue: oneOfType([
    string,
    shape({
      keywords: string,
      addresss: string,
    }),
  ]),
  onSelectAddress: func.isRequired,
  placeholder: string.isRequired,
  enableDelete: bool.isRequired,
  onDelete: func,
  onFocus: func,
  showNumbers: bool.isRequired,
  isSender: bool,
  isShopifyMSD: bool,
  isPickupAddress: bool,
  shopifyRecipient: string,
};

AddressField.defaultProps = {
  initialValue: '',
  onDelete: () => {},
  onFocus: () => {},
  isSender: false,
  isShopifyMSD: false,
  isPickupAddress: false,
  shopifyRecipient: '',
};

export default AddressField;
